import React, { useState } from "react";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Textarea,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Divider,
  VStack,
  Spinner,
} from "@chakra-ui/react";

import { useMutation } from "react-query";

import {
  DownloadIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";

import {
  updateDocumentRequestStatus,
} from "../../services/tankstackApiRequest";


const DocumentRequestDetailsModal = ({
  isOpen,
  onClose,
  request,
  isLoading,
  error,
  onRetry,
  refetch,
}) => {

  // ============================================================
  // STATES
  // ============================================================

  const [selectedRequest, setSelectedRequest] =
    useState(null);

  const [actionType, setActionType] =
    useState("");

  const [isConfirmOpen, setIsConfirmOpen] =
    useState(false);

  const [isCommentOpen, setIsCommentOpen] =
    useState(false);

  const [comment, setComment] =
    useState("");


  // ============================================================
  // STATUS
  // ============================================================

  const status =
    request?.status || "PENDING";


  const statusConfig = {

    PENDING: {
      label: "New",
      color: "orange.600",
      bg: "orange.50",
    },

    PROCESSING: {
      label: "Processing",
      color: "orange.600",
      bg: "orange.50",
    },

    IN_PROGRESS: {
      label: "In Progress",
      color: "orange.600",
      bg: "orange.50",
    },

    COMPLETED: {
      label: "Completed",
      color: "green.600",
      bg: "green.50",
    },

    FAILED: {
      label: "Failed",
      color: "red.600",
      bg: "red.50",
    },

    CANCELLED: {
      label: "Cancelled",
      color: "gray.600",
      bg: "gray.100",
    },

    CLOSED: {
      label: "Closed",
      color: "red.600",
      bg: "red.50",
    },

    REOPENED: {
      label: "Reopened",
      color: "blue.600",
      bg: "blue.50",
    },

    ON_HOLD: {
      label: "On Hold",
      color: "purple.600",
      bg: "purple.50",
    },

  };


  const currentStatus =
    statusConfig[status] ||
    statusConfig.PENDING;


  // ============================================================
  // DATE FORMAT
  // ============================================================

  const formatDate = (date) => {

    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleString(
      "en-GB",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };


  // ============================================================
  // FORMAT KEY
  // ============================================================

  const formatKey = (key) => {

    return key
      .replace(/_/g, " ")
      .replace(
        /\b\w/g,
        (char) => char.toUpperCase()
      );
  };


  // ============================================================
  // FORMAT VALUE
  // ============================================================

  const formatValue = (value) => {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };


  // ============================================================
  // DOWNLOAD DOCUMENT
  // ============================================================

  const handleDownload = (document) => {

    if (!document?.download_url) {
      return;
    }

    window.open(
      document.download_url,
      "_blank",
      "noopener,noreferrer"
    );
  };


  // ============================================================
  // UPDATE STATUS MUTATION
  // ============================================================

  const updateStatusMutation = useMutation(
    ({ id, payload }) =>
      updateDocumentRequestStatus(
        id,
        payload
      ),
    {

      onSuccess: (data) => {

       

        setIsCommentOpen(false);
        setIsConfirmOpen(false);

        setComment("");
        setSelectedRequest(null);
        setActionType("");

        if (refetch) {
          refetch();
        }

        onClose();
      },

      onError: (error) => {

        console.error(
          "Status update failed:",
          error?.message
        );

      },

    }
  );


  // ============================================================
  // REOPEN CLICK
  // ============================================================

  const handleReopen = () => {

    setSelectedRequest(request);

    setActionType("REOPEN");

    setIsConfirmOpen(true);
  };


  // ============================================================
  // CLOSE CLICK
  // ============================================================

  const handleCloseRequest = () => {

    setSelectedRequest(request);

    setActionType("CLOSE");

    setIsConfirmOpen(true);
  };


  // ============================================================
  // CONFIRM YES
  // ============================================================

  const handleConfirmYes = () => {

    setIsConfirmOpen(false);

    setIsCommentOpen(true);
  };


  // ============================================================
  // CONFIRM NO
  // ============================================================

  const handleConfirmNo = () => {

    setIsConfirmOpen(false);

    setSelectedRequest(null);

    setActionType("");

    setComment("");
  };


  // ============================================================
  // SUBMIT STATUS
  // ============================================================

  const handleSubmitStatus = () => {

    if (!selectedRequest?.id) {
      return;
    }


    const payload = {

      status:
        actionType === "REOPEN"
          ? "REOPENED"
          : "CLOSED",

      comment: comment.trim(),

    };
    updateStatusMutation.mutate({
      id: selectedRequest.id,
      payload,
    });

  };


  // ============================================================
  // CLOSE MAIN MODAL
  // ============================================================

  const handleMainClose = () => {

    setSelectedRequest(null);
    setActionType("");
    setComment("");

    onClose();
  };


  return (
    <>

      {/* ======================================================
          MAIN DETAILS MODAL
      ====================================================== */}

      <Modal
        isOpen={isOpen}
        onClose={handleMainClose}
        isCentered
        size="xl"
        scrollBehavior="inside"
      >

        <ModalOverlay bg="blackAlpha.600" />

        <ModalContent
          borderRadius="24px"
          overflow="hidden"
          maxH="90vh"
        >

          {/* HEADER */}

          <ModalHeader
            bg="gray.50"
            borderBottom="1px solid"
            borderColor="gray.200"
            py={5}
          >

            <Flex
              justify="space-between"
              align="center"
              pr={8}
            >

              <Box>

                <Text
                  fontSize="22px"
                  fontWeight="700"
                  color="gray.900"
                >
                  Request Details
                </Text>

                <Text
                  fontSize="14px"
                  color="gray.500"
                  mt={1}
                >
                  {request?.service_request_no ||
                    "Document Request"}
                </Text>

              </Box>


              <Flex
                align="center"
                gap={2}
              >

                <Badge
                  px={4}
                  py={2}
                  borderRadius="20px"
                  bg={currentStatus.bg}
                  color={currentStatus.color}
                  fontSize="14px"
                  fontWeight="700"
                  textTransform="none"
                >
                  {currentStatus.label}
                </Badge>


                {/* REOPEN */}
                {status === "COMPLETED" && (
                  <>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleReopen}
                >
                  Reopen
                </Button>


                {/* CLOSE */}

                <Button
                  size="sm"
                  bg="#b52b2b"
                  color="white"
                  _hover={{
                    bg: "#962424",
                  }}
                  onClick={handleCloseRequest}
                >
                  Close
                </Button>
                </>
)}
              </Flex>

            </Flex>

          </ModalHeader>


          <ModalCloseButton />


          {/* ====================================================
              BODY
          ==================================================== */}

          <ModalBody py={6}>

            {/* LOADING */}

            {isLoading && (

              <Flex
                justify="center"
                align="center"
                direction="column"
                py={16}
              >

                <Spinner
                  size="xl"
                  color="red.500"
                />

                <Text
                  mt={4}
                  color="gray.500"
                >
                  Loading request details...
                </Text>

              </Flex>

            )}


            {/* ERROR */}

            {!isLoading && error && (

              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
              >

                <Text
                  color="red.500"
                  textAlign="center"
                >
                  {error}
                </Text>


                {onRetry && (

                  <Button
                    mt={5}
                    colorScheme="red"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>

                )}

              </Flex>

            )}


            {/* DETAILS */}

            {!isLoading &&
              !error &&
              request && (

              <VStack
                spacing={6}
                align="stretch"
              >

                {/* REQUEST INFORMATION */}

                <Box>

                  <Text
                    fontSize="24px"
                    fontWeight="700"
                    mb={4}
                  >
                    Request Information
                  </Text>


                  <VStack
                    spacing={3}
                    align="stretch"
                  >

                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Request ID
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {request.id || "-"}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Request Number
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {request.service_request_no ||
                          "-"}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Service Type
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {request.service_type_name ||
                          "-"}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Document Format
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {request.requested_document_format ||
                          "-"}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Priority
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {request.priority || "-"}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Created At
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {formatDate(
                          request.created_at
                        )}
                      </Text>

                    </Flex>


                    <Flex
                      justify="space-between"
                    >

                      <Text color="gray.500" fontSize="16px">
                        Updated At
                      </Text>

                      <Text fontWeight="600" fontSize="15px">
                        {formatDate(
                          request.updated_at
                        )}
                      </Text>

                    </Flex>

                  </VStack>

                </Box>


                <Divider />


                {/* SUBMITTED INFORMATION */}

                {request.payload &&
                  Object.keys(request.payload).length >
                    0 && (

                  <Box>

                    <Text
                      fontSize="18px"
                      fontWeight="700"
                      mb={4}
                    >
                      Submitted Information
                    </Text>


                    <Box
                      bg="gray.50"
                      borderRadius="16px"
                      p={4}
                    >

                      <VStack
                        spacing={3}
                        align="stretch"
                      >

                        {Object.entries(
                          request.payload
                        ).map(
                          ([key, value]) => (

                            <Flex
                              key={key}
                              justify="space-between"
                              gap={4}
                            >

                              <Text
                                color="gray.500"
                                fontSize="16px"
                              >
                                {formatKey(key)}
                              </Text>

                              <Text
                                fontWeight="600"
                                textAlign="right"
                                wordBreak="break-word"
                                fontSize="15px"
                              >
                                {formatValue(value)}
                              </Text>

                            </Flex>

                          )
                        )}

                      </VStack>

                    </Box>

                  </Box>

                )}


                <Divider />


                {/* DOCUMENTS */}

                <Box>

                  <Text
                    fontSize="18px"
                    fontWeight="700"
                    mb={4}
                  >
                    Documents
                  </Text>


                  {request.documents?.length > 0 ? (

                    <VStack
                      spacing={3}
                      align="stretch"
                    >

                      {request.documents.map(
                        (document) => (

                          <Flex
                            key={document.id}
                            justify="space-between"
                            align="center"
                            bg="gray.50"
                            borderRadius="14px"
                            p={4}
                          >

                            <Box>

                              <Text fontWeight="600" fontSize="16px">
                                {document.file_name ||
                                  "Document"}
                              </Text>

                              <Text
                                fontSize="15px"
                                color="gray.500"
                               
                              >
                                {document.file_type ||
                                  "File"}
                              </Text>

                            </Box>


                            {document.download_url && (

                              <Button
                                leftIcon={
                                  <DownloadIcon />
                                }
                                colorScheme="green"
                                size="sm"
                                onClick={() =>
                                  handleDownload(
                                    document
                                  )
                                }
                              >
                                Download
                              </Button>

                            )}

                          </Flex>

                        )
                      )}

                    </VStack>

                  ) : (

                    <Box
                      bg="gray.50"
                      borderRadius="14px"
                      p={4}
                      fontSize="16px"
                    >

                      <Text color="gray.500">
                        No documents available.
                      </Text>

                    </Box>

                  )}

                </Box>


                {/* STATUS HISTORY */}

                {request.status_history?.length >
                  0 && (

                  <>

                    <Divider />

                    <Box>

                      <Text
                        fontSize="18px"
                        fontWeight="700"
                        mb={4}
                      >
                        Status History
                      </Text>


                      <VStack
                        spacing={3}
                        align="stretch"
                      >

                        {request.status_history.map(
                          (history) => (

                            <Flex
                              key={history.id}
                              gap={3}
                              align="flex-start"
                            >

                              <CheckCircleIcon
                                mt={1}
                                color={
                                  history.new_status ===
                                  "COMPLETED"
                                    ? "green.500"
                                    : "red.500"
                                }
                              />


                              <Box flex="1">

                                <Flex
                                  justify="space-between"
                                >

                                  <Text fontWeight="600">

                                    {history.old_status
                                      ? `${history.old_status} → `
                                      : ""}

                                    {history.new_status}

                                  </Text>


                                  <Text
                                    fontSize="12px"
                                    color="gray.400"
                                  >

                                    {formatDate(
                                      history.created_at
                                    )}

                                  </Text>

                                </Flex>


                                {history.remarks && (

                                  <Text
                                    fontSize="13px"
                                    color="gray.500"
                                    mt={1}
                                  >
                                    {history.remarks}
                                  </Text>

                                )}

                              </Box>

                            </Flex>

                          )
                        )}

                      </VStack>

                    </Box>

                  </>

                )}

              </VStack>

            )}

          </ModalBody>

        </ModalContent>

      </Modal>


      {/* ======================================================
          CONFIRMATION MODAL
      ====================================================== */}

      <Modal
        isOpen={isConfirmOpen}
        onClose={handleConfirmNo}
        isCentered
      >

        <ModalOverlay />

        <ModalContent>

          <ModalHeader>
            {actionType === "REOPEN"
              ? "Reopen Request"
              : "Close Request"}
          </ModalHeader>


          <ModalCloseButton />


          <ModalBody>

            <Text>

              {actionType === "REOPEN"
                ? "Are you sure you want to reopen this request?"
                : "Are you sure you want to close this request?"}

            </Text>

          </ModalBody>


          <ModalFooter>

            <Button
              variant="outline"
              mr={3}
              onClick={handleConfirmNo}
            >
              No
            </Button>


            <Button
              bg="#b52b2b"
              color="white"
              _hover={{
                bg: "#962424",
              }}
              onClick={handleConfirmYes}
            >
              Yes
            </Button>

          </ModalFooter>

        </ModalContent>

      </Modal>


      {/* ======================================================
          COMMENT MODAL
      ====================================================== */}

      <Modal
        isOpen={isCommentOpen}
        onClose={() => {

          setIsCommentOpen(false);
          setComment("");

        }}
        isCentered
      >

        <ModalOverlay />

        <ModalContent>

          <ModalHeader>

            {actionType === "REOPEN"
              ? "Reopen Request"
              : "Close Request"}

          </ModalHeader>


          <ModalCloseButton />


          <ModalBody>

            <Text mb={3}>

              Please enter your comment

            </Text>


            <Textarea
              placeholder="Enter your comment..."
              value={comment}
              onChange={(e) =>
                setComment(e.target.value)
              }
            />

          </ModalBody>


          <ModalFooter>

            <Button
              variant="outline"
              mr={3}
              onClick={() => {

                setIsCommentOpen(false);
                setComment("");

              }}
            >
              Cancel
            </Button>


            <Button
              bg="#b52b2b"
              color="white"
              _hover={{
                bg: "#962424",
              }}
              onClick={handleSubmitStatus}
              isLoading={
                updateStatusMutation.isLoading
              }
            >
              Submit
            </Button>

          </ModalFooter>

        </ModalContent>

      </Modal>

    </>
  );
};

export default DocumentRequestDetailsModal;